import { useEffect, useState } from "react";
import { AttributeType } from "../../../clients/Purchaser/Attributes";
import useDebounce from "../../Common/Hooks/useDebounce";
import type { SetAttributeFilter } from "../types";

// The attribute types that can be filtered on whether their value contains some text.
type ContainsFilterType = AttributeType.PHONE | AttributeType.ADDRESS;

/**
 * A "contains" search for a filter submenu's text field. The backend does the fuzzy substring match. Returns
 * the field's text and its setter; the filter is written once the text settles.
 */
export function useContainsSearch(props: {
    type: ContainsFilterType;
    attributeId: number;
    // The contains query already applied, if any.
    currentValue?: string;
    setAttributeFilter: SetAttributeFilter;
}) {
    const { type, attributeId, currentValue, setAttributeFilter } = props;
    const [search, setSearch] = useState(currentValue ?? "");
    const debouncedSearch = useDebounce(search, 300);

    // Commit the contains query when it settles. Skips the no-op mount case so we
    // never clobber an existing set/not-set filter on open.
    useEffect(() => {
        const current = currentValue ?? "";
        if (debouncedSearch === current) return;
        if (debouncedSearch.trim()) {
            setAttributeFilter({ type, attributeId, value: debouncedSearch });
        } else {
            setAttributeFilter({ type, attributeId, value: "" }, true);
        }
    }, [debouncedSearch]);

    return [search, setSearch] as const;
}
